import { useCallback, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import {
  selectSelectedSlideId,
  setSelectedSlideId,
} from "../../../app/slices/GlobalSlice";
import { selectSelectedElement } from "../../../app/slices/TrainingSlice";
import {
  changePowerSlideTemplate,
  IPowerSlide,
  resolvePowerSlideTheme,
} from "../../../models/interactions/PowerSlideHelper";
import {
  getPowerSlideTemplate,
  PowerSlideTemplateId,
} from "../../../models/interactions/PowerSlideTemplates";
import { useTrainingLocalization } from "../../trainingLocalization/TrainingLocalizationHooks";
import PowerSlideCanvas from "./PowerSlideCanvas";
import PowerSlidePager from "./PowerSlidePager";
import PowerSlideRail from "./PowerSlideRail";
import PowerSlideTemplatePicker from "./PowerSlideTemplatePicker";
import { usePowerSlideDeck } from "./usePowerSlideDeck.hook";

type PickerMode = "add" | "change";

export default function PowerSlidePreview() {
  const deck = useSelector(selectSelectedElement) as IPowerSlide | undefined;
  const storedSlideId = useSelector(selectSelectedSlideId);
  const theme = resolvePowerSlideTheme(deck);
  const dispatch = useDispatch();
  const { getValue, changeValue, getNewKey, GetArrayOfKeys } =
    useTrainingLocalization();
  const { t } = useTranslation();
  const [pickerMode, setPickerMode] = useState<PickerMode | undefined>();
  const rootRef = useRef<HTMLDivElement>(null);

  const {
    addSlide,
    updateSlide,
    duplicateSlide,
    copySlide,
    pasteSlide,
    deleteSlide,
    moveSlide,
  } = usePowerSlideDeck(deck);

  const slides = deck?.slides ?? [];
  // A stored id from another deck, or from a slide since deleted, falls back to
  // the first slide rather than leaving the canvas blank.
  const selectedSlide =
    slides.find((slide) => slide.id === storedSlideId) ?? slides[0];
  const selectedTemplate = selectedSlide
    ? getPowerSlideTemplate(selectedSlide.templateId)
    : undefined;

  /**
   * Add slide asks which template, because choosing is the point of the action.
   * What used to happen automatically - the picker appearing the moment a deck
   * was created, before there was anything to look at - is gone: the factory
   * now builds the first slide, so this only ever runs on a deliberate click.
   */
  const handleAdd = useCallback(() => setPickerMode("add"), []);

  const selectByOffset = useCallback(
    (offset: number) => {
      if (slides.length === 0) return;
      const current = slides.findIndex(
        (slide) => slide.id === selectedSlide?.id
      );
      const next = Math.max(
        0,
        Math.min(slides.length - 1, (current < 0 ? 0 : current) + offset)
      );
      dispatch(setSelectedSlideId(slides[next].id));
    },
    [dispatch, selectedSlide?.id, slides]
  );

  /**
   * Keyboard navigation is a requirement rather than a nicety: the on-canvas
   * stepping arrows were removed in favour of the rail plus the keyboard.
   * Ignored while a text field has focus, so typing in a slide still works.
   */
  const onKeyDown = useCallback(
    (event: React.KeyboardEvent) => {
      const target = event.target as HTMLElement;
      const isTyping =
        target.tagName === "TEXTAREA" ||
        target.tagName === "INPUT" ||
        target.isContentEditable;

      if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === "v") {
        if (isTyping) return;
        event.preventDefault();
        pasteSlide(selectedSlide?.id);
        return;
      }
      if (isTyping) return;

      switch (event.key) {
        case "ArrowLeft":
        case "ArrowUp":
          event.preventDefault();
          selectByOffset(-1);
          break;
        case "ArrowRight":
        case "ArrowDown":
          event.preventDefault();
          selectByOffset(1);
          break;
        case "Home":
          event.preventDefault();
          selectByOffset(-slides.length);
          break;
        case "End":
          event.preventDefault();
          selectByOffset(slides.length);
          break;
        default:
          break;
      }
    },
    [pasteSlide, selectByOffset, selectedSlide?.id, slides.length]
  );

  const handleChangeTemplate = useCallback(
    (templateId: PowerSlideTemplateId) => {
      if (!selectedSlide) return;
      updateSlide(
        changePowerSlideTemplate(selectedSlide, templateId, GetArrayOfKeys)
      );
    },
    [selectedSlide, updateSlide, GetArrayOfKeys]
  );

  if (!deck) return null;

  return (
    /*
     * Absolutely filling the overlay rather than relying on the parent's
     * height: the preview wrapper only sets h-full for 360 trainings, so a 3D
     * deck would otherwise collapse to its content and the rail would float
     * mid-screen instead of docking.
     */
    <div
      ref={rootRef}
      tabIndex={-1}
      onKeyDown={onKeyDown}
      aria-label="powerslide-editor"
      className="absolute inset-0 flex flex-col overflow-hidden outline-none"
    >
      {/* A deck plays full screen, so the editor gives it a ground of its own
          rather than leaving slides floating over the scene behind. The
          viewport token is the app's own preview ground and follows the
          editor's light and dark themes. */}
      <div className="flex min-h-0 flex-1 flex-col items-center justify-center gap-[10px] bg-viewport p-[20px]">
        {selectedSlide && (
          <PowerSlidePager
            slide={selectedSlide}
            index={slides.findIndex((slide) => slide.id === selectedSlide.id)}
            getValue={getValue}
            onChangeTemplate={() => setPickerMode("change")}
            onAdd={handleAdd}
          />
        )}
        {/* No aspect ratio here: the stage measures this box and letterboxes
            itself inside it, so the slide can never push the pill out of view. */}
        <div className="min-h-0 w-full flex-1">
          {selectedSlide ? (
            <PowerSlideCanvas
              slide={selectedSlide}
              getValue={getValue}
              changeValue={changeValue}
              onUpdateSlide={updateSlide}
              getNewKey={getNewKey}
              theme={theme}
            />
          ) : (
            <button
              type="button"
              aria-label="powerslide-add-first-slide"
              onClick={handleAdd}
              className="flex h-full w-full flex-col items-center justify-center gap-[6px] rounded-[6px] border-2 border-dashed border-creator-border text-creator-text-sub hover:border-creator-primary"
            >
              <span className="text-[16px] font-semibold text-creator-text">
                {t("powerslide.empty.title", {
                  defaultValue: "Add your first slide",
                })}
              </span>
              <span className="text-[12px]">
                {t("powerslide.empty.subtitle", {
                  defaultValue: "Pick a template to get started",
                })}
              </span>
            </button>
          )}
        </div>
      </div>
      <div className="shrink-0 border-t border-solid border-creator-border bg-creator-surface">
        <PowerSlideRail
          slides={slides}
          selectedSlideId={selectedSlide?.id}
          getValue={getValue}
          onSelect={(slideId) => dispatch(setSelectedSlideId(slideId))}
          onStep={selectByOffset}
          onAdd={handleAdd}
          onDuplicate={duplicateSlide}
          onCopy={copySlide}
          onDelete={deleteSlide}
          onMove={moveSlide}
          theme={theme}
        />
      </div>
      {pickerMode && (
        <PowerSlideTemplatePicker
          isOpen={true}
          onClose={() => setPickerMode(undefined)}
          onSelect={
            pickerMode === "add"
              ? (templateId) => addSlide(templateId, selectedSlide?.id)
              : handleChangeTemplate
          }
          currentTemplateId={
            pickerMode === "change" ? selectedSlide?.templateId : undefined
          }
          theme={theme}
        />
      )}
    </div>
  );
}
